'use client';

import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
    mixpanel: any;
  }
}

// ─── Google Analytics ───────────────────────────────────────
export function useGoogleAnalytics() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
    if (!measurementId || measurementId === 'your-google-analytics-id-here') return;

    if (!window.dataLayer) {
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      script.async = true;
      document.head.appendChild(script);
      window.dataLayer = [];
      window.gtag = function (...args: any[]) {
        window.dataLayer.push(args);
      };
      window.gtag('js', new Date());
      window.gtag('config', measurementId, { send_page_view: false });
    }

    const url = pathname + (searchParams.toString() ? `?${searchParams}` : '');
    if (window.gtag) {
      window.gtag('event', 'page_view', { page_path: url });
    }
  }, [pathname, searchParams]);
}

export function trackGAEvent(eventName: string, eventParams: Record<string, any> = {}) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
}

// ─── Mixpanel ───────────────────────────────────────────────
export function initMixpanel() {
  const token = process.env.NEXT_PUBLIC_MIXPANEL_TOKEN;
  if (!token || token === 'your-mixpanel-token-here') return;
  if (typeof window === 'undefined') return;
  if (window.mixpanel) return;

  // Mixpanel stub loader
  (function (c: any, a: any) {
    window.mixpanel = a;
    a.__loaded = false;
    a.config = {};
    a.people = {};
    const b: any = {};
    const d = [
      'init', 'track', 'identify', 'alias', 'track_links', 'track_forms',
      'register', 'register_once', 'unregister', 'name_tag', 'set_config',
      'reset', 'people.set', 'people.increment',
    ];
    for (let i = 0; i < d.length; i++) {
      const f = d[i];
      const g = f.split('.');
      if (g.length === 2) {
        b[g[0]] = b[g[0]] || {};
        b[g[0]][g[1]] = (function (e: string) {
          return function (...args: any[]) {
            a._q = a._q || [];
            a._q.push([e, ...args]);
          };
        })(f);
      } else {
        b[f] = (function (e: string) {
          return function (...args: any[]) {
            a._q = a._q || [];
            a._q.push([e, ...args]);
          };
        })(f);
      }
    }
    Object.assign(a, b);

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = 'https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js';
    script.onload = () => {
      window.mixpanel.init(token, { debug: false, track_pageview: true });
      window.mixpanel.__loaded = true;
    };
    document.head.appendChild(script);
  })(document, window.mixpanel || {});
}

export function trackMixpanelEvent(eventName: string, properties: Record<string, any> = {}) {
  if (typeof window !== 'undefined' && window.mixpanel && window.mixpanel.__loaded) {
    window.mixpanel.track(eventName, properties);
  }
}

export function identifyMixpanelUser(userId: string, traits: Record<string, any> = {}) {
  if (typeof window !== 'undefined' && window.mixpanel && window.mixpanel.__loaded) {
    window.mixpanel.identify(userId);
    if (Object.keys(traits).length > 0) {
      window.mixpanel.people.set(traits);
    }
  }
}

// ─── Combined tracker ────────────────────────────────────────
export function trackEvent(eventName: string, properties: Record<string, any> = {}) {
  trackGAEvent(eventName, properties);
  trackMixpanelEvent(eventName, properties);
}
